import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import { useMemo } from 'react';
import StoneXChart from '../../../StoneX/StoneXChart';
import { chartAxisValue, FiveYearStatistics, SeasonSeries, STORAGE_REGION_LABELS, StorageRegion } from './EiaGasStorageLogic';

HighchartsMore(Highcharts);

// The latest season is drawn first and darkest; older seasons fade through the palette.
const SEASON_COLORS = ['#1a237e', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#6d4c41', '#c0ca33', '#d81b60', '#3949ab', '#7cb342', '#f4511e'];
const AVERAGE_COLOR = '#37474f';
const RANGE_COLOR = 'rgba(176, 190, 197, 0.45)';

export interface EiaGasStorageChartProps {
  region: StorageRegion;
  seasons: SeasonSeries[];
  fiveYear: FiveYearStatistics[];
  showAverage: boolean;
  showRange: boolean;
  /** Week ending of the latest report, shown in the subtitle. */
  latestWeekEnding?: Date;
  yAxisMinimum?: number;
  yAxisMaximum?: number;
  yAxisStep?: number;
  onChartReady?: (chart: Highcharts.Chart) => void;
}

export default function EiaGasStorageChart(props: EiaGasStorageChartProps) {
  const { region, seasons, fiveYear, showAverage, showRange, latestWeekEnding, yAxisMinimum, yAxisMaximum, yAxisStep, onChartReady } = props;

  const options = useMemo<Highcharts.Options>(() => {
    const series: Highcharts.SeriesOptionsType[] = [];

    if (showRange && fiveYear.length > 0) {
      series.push({
        type: 'arearange',
        name: '5-Year Range',
        data: fiveYear.map(statistic => [chartAxisValue(statistic.dayOfSeason), statistic.minimum, statistic.maximum]),
        color: RANGE_COLOR,
        fillColor: RANGE_COLOR,
        lineWidth: 0,
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 0,
      });
    }

    if (showAverage && fiveYear.length > 0) {
      series.push({
        type: 'line',
        name: '5-Year Average',
        data: fiveYear.map(statistic => [chartAxisValue(statistic.dayOfSeason), statistic.average]),
        color: AVERAGE_COLOR,
        dashStyle: 'ShortDot',
        lineWidth: 2,
        marker: { enabled: false },
        zIndex: 1,
      });
    }

    seasons.forEach((season, index) => {
      series.push({
        type: 'line',
        name: season.label,
        data: season.points.map(point => ({ x: chartAxisValue(point.dayOfSeason), y: point.volume, custom: { weekEnding: point.weekEnding, netChange: point.netChange } })),
        color: SEASON_COLORS[index % SEASON_COLORS.length],
        lineWidth: index === 0 ? 3 : 2,
        marker: { enabled: false, symbol: 'circle' },
        zIndex: seasons.length - index + 1,
      });
    });

    const subtitleParts = [STORAGE_REGION_LABELS[region]];
    if (latestWeekEnding) subtitleParts.push(`For week ended ${dayjs(latestWeekEnding).format('MMMM D, YYYY')}`);

    return {
      chart: { zooming: { type: 'x' }, spacingTop: 12 },
      title: { text: 'Working Natural Gas in Underground Storage' },
      subtitle: { text: subtitleParts.join(' · ') },
      credits: { enabled: true, text: 'Source: EIA Weekly Natural Gas Storage Report', href: 'https://ir.eia.gov/ngs/ngs.html' },
      xAxis: {
        type: 'datetime',
        min: chartAxisValue(0),
        max: chartAxisValue(365),
        tickInterval: 30 * 24 * 3600 * 1000,
        labels: { format: '{value:%b}' },
        gridLineWidth: 1,
        crosshair: true,
      },
      yAxis: {
        title: { text: 'Bcf' },
        min: yAxisMinimum ?? null,
        max: yAxisMaximum ?? null,
        tickInterval: yAxisStep ?? undefined,
        labels: { format: '{value:,.0f}' },
      },
      legend: { align: 'right', verticalAlign: 'middle', layout: 'vertical' },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const points = (this.points ?? []) as Highcharts.TooltipFormatterContextObject[];
          const rows = points.map(point => {
            const custom = (point.point as unknown as { custom?: { weekEnding?: Date; netChange?: number | null } }).custom;
            const weekEnding = custom?.weekEnding ? ` (${dayjs(custom.weekEnding).format('MMM D, YYYY')})` : '';
            const netChange = custom?.netChange != null ? ` <span style="color:#757575">${custom.netChange > 0 ? '+' : ''}${Highcharts.numberFormat(custom.netChange, 0)}</span>` : '';
            return `<span style="color:${point.color}">●</span> ${point.series.name}${weekEnding}: <b>${Highcharts.numberFormat(point.y ?? 0, 0)}</b>${netChange}`;
          });
          return rows.join('<br/>');
        },
      },
      plotOptions: { series: { animation: false, states: { inactive: { opacity: 1 } } } },
      exporting: { enabled: true, filename: `EIA gas storage ${STORAGE_REGION_LABELS[region]}` },
      series,
    };
  }, [region, seasons, fiveYear, showAverage, showRange, latestWeekEnding, yAxisMinimum, yAxisMaximum, yAxisStep]);

  return <StoneXChart options={options} height={520} onChartReady={onChartReady} />;
}
